<script setup lang="ts">
type ThumbnailVariant = 'action' | 'waiting' | 'completed'

withDefaults(
  defineProps<{
    variant?: ThumbnailVariant
  }>(),
  {
    variant: 'action',
  },
)

// Generate random line widths for visual variation between instances
const randomWidth = () => Math.floor(Math.random() * 40) + 60 // 60-100%
const titleWidth = `${Math.floor(Math.random() * 30) + 40}%` // 40-70%
const lineCount = Math.floor(Math.random() * 3) + 5 // 5-7 lines
const lines = Array.from({ length: lineCount }, () => `${randomWidth()}%`)
</script>

<template>
  <div class="relative flex-shrink-0">
    <!-- Action Required Thumbnail -->
    <template v-if="variant === 'action'">
      <div class="document-stack">
        <!-- Back pages (stack effect) -->
        <div
          class="document-page document-page--back-2 absolute h-36 w-28 rounded-lg bg-white ring-1 ring-gray-200"
        ></div>
        <div
          class="document-page document-page--back-1 absolute h-36 w-28 rounded-lg bg-white ring-1 ring-gray-200"
        ></div>
        <!-- Front page -->
        <div
          class="document-thumbnail document-thumbnail--action relative h-36 w-28 rounded-lg bg-gradient-to-b from-white to-gray-50 ring-1 ring-gray-200"
        >
          <!-- Paper content lines -->
          <div class="absolute inset-x-3 top-4">
            <!-- Fake title -->
            <div class="mb-2 h-1.5 rounded-sm bg-gray-200" :style="{ width: titleWidth }"></div>
            <!-- Fake body lines -->
            <div class="space-y-1">
              <div v-for="(width, i) in lines" :key="i" class="h-1 rounded-sm bg-gray-100" :style="{ width }"></div>
            </div>
          </div>
          <!-- Signature line area -->
          <div class="absolute bottom-3 left-6 right-3">
            <div class="flex items-end gap-1">
              <span class="text-[8px] font-medium text-[#52B7B9]">&#10007;</span>
              <div class="h-px flex-1 border-b border-dashed border-[#52B7B9]"></div>
            </div>
            <div class="mt-0.5 h-1 w-12 rounded-sm bg-gray-100"></div>
          </div>
          <!-- Sign here tab -->
          <div class="absolute -left-2 bottom-4 flex items-center">
            <div class="flex h-3 items-center rounded-l-sm bg-[#52B7B9] px-1 pb-[1px]">
              <span class="text-[5px] font-bold uppercase leading-none tracking-widest text-white">sign</span>
            </div>
            <div class="h-0 w-0 border-y-[6px] border-l-[5px] border-y-transparent border-l-[#52B7B9]"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Waiting Thumbnail -->
    <template v-else-if="variant === 'waiting'">
      <div class="document-stack document-stack--muted">
        <!-- Back pages (stack effect) -->
        <div
          class="document-page document-page--back-2 absolute h-36 w-28 rounded-lg bg-white/60 ring-1 ring-gray-200"
        ></div>
        <div
          class="document-page document-page--back-1 absolute h-36 w-28 rounded-lg bg-white/80 ring-1 ring-gray-200"
        ></div>
        <!-- Front page -->
        <div
          class="document-thumbnail document-thumbnail--waiting relative h-36 w-28 overflow-hidden rounded-lg bg-gradient-to-b from-gray-50 to-gray-100 opacity-60 ring-1 ring-gray-200"
        >
          <!-- Paper content lines -->
          <div class="absolute inset-x-3 top-4">
            <!-- Fake title -->
            <div class="mb-2 h-1.5 rounded-sm bg-gray-300/70" :style="{ width: titleWidth }"></div>
            <!-- Fake body lines -->
            <div class="space-y-1">
              <div v-for="(width, i) in lines" :key="i" class="h-1 rounded-sm bg-gray-200/70" :style="{ width }"></div>
            </div>
          </div>
          <!-- Signature line (muted) -->
          <div class="absolute inset-x-3 bottom-3">
            <div class="h-px w-full border-b border-dashed border-gray-300"></div>
          </div>
        </div>
      </div>
      <!-- Waiting indicator -->
      <div class="absolute -right-1 -top-1 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-gray-300">
        <svg class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    </template>

    <!-- Completed Thumbnail -->
    <template v-else-if="variant === 'completed'">
      <div class="document-stack">
        <!-- Back pages (stack effect) -->
        <div
          class="document-page document-page--back-2 absolute h-36 w-28 rounded-lg bg-white ring-1 ring-green-200"
        ></div>
        <div
          class="document-page document-page--back-1 absolute h-36 w-28 rounded-lg bg-white ring-1 ring-green-200"
        ></div>
        <!-- Front page -->
        <div
          class="document-thumbnail document-thumbnail--complete relative h-36 w-28 overflow-hidden rounded-lg bg-gradient-to-b from-white to-green-50/50 ring-1 ring-green-200/50"
        >
          <!-- Paper content lines -->
          <div class="absolute inset-x-3 top-4">
            <!-- Fake title -->
            <div class="mb-2 h-1.5 rounded-sm bg-gray-200" :style="{ width: titleWidth }"></div>
            <!-- Fake body lines -->
            <div class="space-y-1">
              <div v-for="(width, i) in lines" :key="i" class="h-1 rounded-sm bg-gray-100" :style="{ width }"></div>
            </div>
          </div>
          <!-- Completed stamp overlay -->
          <div class="absolute inset-0 flex items-center justify-center">
            <div
              class="flex h-12 w-12 items-center justify-center rounded-full border-2 border-green-500/40 bg-green-500/10"
            >
              <svg
                class="checkmark-icon h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path class="checkmark-path" stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Document stack container */
.document-stack {
  position: relative;
  /* Add padding to accommodate the offset back pages */
  padding-bottom: 4px;
  padding-right: 4px;
}

/* Back page positioning */
.document-page {
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.document-page--back-2 {
  top: 4px;
  left: 5px;
  transform: rotate(2deg);
}

.document-page--back-1 {
  top: 2px;
  left: 3px;
  transform: rotate(1deg);
}

/* Document thumbnail base styles */
.document-thumbnail {
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  transform-style: preserve-3d;
}

/* Action required thumbnail - subtle grow on hover */
.group:hover .document-thumbnail--action {
  transform: scale(1.02);
}

/* Fan out back pages on hover for action variant */
.group:hover .document-stack .document-page--back-2 {
  transform: rotate(2.5deg) translate(2px, 0.5px) scale(0.98);
}

.group:hover .document-stack .document-page--back-1 {
  transform: rotate(1.5deg) translate(1px, 0.25px) scale(0.99);
}

/* Checkmark draw animation */
.checkmark-path {
  stroke-dasharray: 24;
  stroke-dashoffset: 24;
  animation: checkmark-draw 0.4s ease-out 0.2s forwards;
}

@keyframes checkmark-draw {
  to {
    stroke-dashoffset: 0;
  }
}

/* Checkmark icon bounce */
.checkmark-icon {
  animation: checkmark-bounce 0.5s ease-out 0.1s both;
}

@keyframes checkmark-bounce {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
